import { alpha, Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

// Element type of MUI's `sx` array form — not the nested SxProps union, which is what these
// would become at the call sites they're spread into.
type SxArrayEntry = SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>);

export type SendSecondaryButtonSize = 'small' | 'medium';

export const sendButtonRootSx = (size: SendSecondaryButtonSize): SxArrayEntry =>
  size === 'small'
    ? (theme) => ({
        minHeight: '1.875rem',
        [theme.breakpoints.down('md')]: { minHeight: '2.25rem' },
        padding: theme.spacing(1, 2),
        gap: theme.spacing(0.875),
        // icon box and lineHeight:1 text box differ in height — center, don't stretch
        alignItems: 'center',
        // Mockup's send-split button is flat in every state. Matched on `&.MuiButton-contained`
        // to reach StyledButton's own shadow rule specificity; a bare `boxShadow` loses to it.
        '&.MuiButton-contained': {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '&.Mui-disabled': { boxShadow: 'none' },
        },
      })
    : {};

export const secondaryButtonRootSx = (size: SendSecondaryButtonSize): SxArrayEntry =>
  size === 'small'
    ? (theme) => ({
        minHeight: '1.875rem',
        [theme.breakpoints.down('md')]: { minHeight: '2.25rem' },
        padding: theme.spacing(0.75, 1),
        borderRadius: '0.4375rem',
        gap: theme.spacing(0.75),
        alignItems: 'center',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
        },
      })
    : {};

const compactLabelBaseSx = () => ({
  fontWeight: 700,
  letterSpacing: '0.04em',
  lineHeight: 1,
  fontSize: '0.71875rem',
  // ascent/descent asymmetry leaves all-caps ink 1px above the box center — nudge it back
  transform: 'translateY(1px)',
  display: { xs: 'none', md: 'initial' } as const,
});

const defaultLabelBaseSx = (theme: Theme) => ({
  fontWeight: 700,
  letterSpacing: theme.typography.overline.letterSpacing,
  display: { xs: 'none', md: 'initial' } as const,
});

export const sendButtonLabelSx =
  (size: SendSecondaryButtonSize): SxArrayEntry =>
  (theme) =>
    size === 'small'
      ? { ...compactLabelBaseSx(), marginLeft: 0 }
      : { ...defaultLabelBaseSx(theme), marginLeft: '0.5rem' };

export const secondaryButtonLabelSx =
  (size: SendSecondaryButtonSize, hasIcon: boolean): SxArrayEntry =>
  (theme) =>
    size === 'small'
      ? { ...compactLabelBaseSx(), marginRight: 0 }
      : { ...defaultLabelBaseSx(theme), marginRight: hasIcon ? '0.5rem' : 0 };
